import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * AuthCallback — handles the Emergent-managed Google OAuth return.
 *
 * On successful Google auth the user lands at `${origin}/dashboard#session_id=X`.
 * AppRouter (in App.js) intercepts that hash synchronously during render and
 * mounts THIS component instead of the normal route tree. That means every
 * effect below runs BEFORE any protected page ever calls /api/auth/me — no
 * race condition, no premature 401.
 *
 * We exchange the session_id via POST /api/auth/google/session, store the
 * returned SAGE JWT in localStorage (same key the rest of the app uses), and
 * navigate to the dashboard with `replace: true` so the hash disappears from
 * the URL bar (and Back doesn't retry the exchange).
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
const AuthCallback = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // useRef (not useState) is essential under React.StrictMode — the effect
  // fires twice and would otherwise POST the same session_id twice. The ref
  // is set SYNCHRONOUSLY at the top of the effect before any await.
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const params = new URLSearchParams(location.hash.replace(/^#/, ''));
    const sessionId = params.get('session_id');
    if (!sessionId) {
      navigate('/login', { replace: true });
      return;
    }

    (async () => {
      try {
        const res = await axios.post(`${API}/auth/google/session`, {
          session_id: sessionId,
        });
        localStorage.setItem('sage_token', res.data.access_token);
        localStorage.setItem('sage_user', JSON.stringify(res.data.user));
        toast.success('Signed in with Google');
        navigate('/dashboard', { replace: true });
      } catch (err) {
        const detail = err?.response?.data?.detail || 'Google sign-in failed';
        toast.error(detail, { duration: 6000 });
        navigate('/login', { replace: true });
      }
    })();
    // Mount-only — the session_id is a one-shot token.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="min-h-screen bg-obsidian flex items-center justify-center"
      data-testid="auth-callback"
    >
      <div className="overlay-noise" />
      <div className="relative z-10 text-center">
        <div className="w-8 h-8 border-2 border-white/20 border-t-white/70 rounded-full animate-spin mx-auto mb-4" />
        <p
          className="text-zinc-400 text-sm font-mono uppercase tracking-widest"
          style={{ fontFamily: 'monospace' }}
        >
          Signing you in…
        </p>
      </div>
    </div>
  );
};

export default AuthCallback;
